import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Quote } from "lucide-react";
import { getInsight } from "@/lib/api";

export default function InsightArticle() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    getInsight(slug).then(setA).catch(() => setError(true));
  }, [slug]);

  if (error)
    return (
      <main className="min-h-screen pt-40 px-6 text-center" data-testid="insight-error">
        <h1 className="font-serif text-3xl font-semibold mb-6">That insight does not exist.</h1>
        <Link to="/insights" data-testid="insight-error-back" className="text-clay font-semibold">Back to insights</Link>
      </main>
    );

  if (!a)
    return (
      <main className="min-h-screen pt-48 flex justify-center" data-testid="insight-loading">
        <Loader2 className="h-8 w-8 animate-spin text-clay" />
      </main>
    );

  return (
    <main className="min-h-screen pt-28 pb-24" data-testid="insight-article">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="mx-auto max-w-3xl px-4 sm:px-6">
        <Link to="/insights" data-testid="insight-back" className="inline-flex items-center gap-2 text-sm font-semibold text-ink/60 hover:text-clay transition-colors mb-8 min-h-[44px]">
          <ArrowLeft className="h-4 w-4" /> All insights
        </Link>
        <div className="flex flex-wrap items-center gap-3 mb-5 font-mono text-[10px] uppercase tracking-[0.18em] text-slatem">
          <span className="text-clay">{a.label}</span><span>·</span><span className="text-sage">Grade {a.grade}</span><span>·</span>
          <span>{a.read_minutes} min read</span><span>·</span><span>reviewed {a.review_date}</span>
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[0.98]" data-testid="insight-title">{a.title}</h1>
        <p className="mt-5 text-lg text-slatem leading-relaxed">{a.dek}</p>
      </motion.div>

      <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.2 }}
        className="mx-auto max-w-5xl px-4 sm:px-6 mt-10">
        <div className="overflow-hidden rounded-3xl border border-ink/12">
          <img src={a.image} alt={a.title} className="w-full aspect-[21/9] object-cover" data-testid="insight-hero-image" />
        </div>
      </motion.div>

      <div className="mx-auto max-w-3xl px-4 sm:px-6 mt-12">
        <div className="flex items-start gap-4 rounded-3xl bg-ink text-bone p-6 sm:p-8 mb-12" data-testid="insight-takeaway">
          <Quote className="h-6 w-6 text-clay shrink-0 mt-1" />
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage mb-2">the one-line answer</p>
            <p className="font-serif text-2xl sm:text-3xl font-bold uppercase tracking-tight leading-tight">{a.takeaway}</p>
          </div>
        </div>

        <div className="space-y-10">
          {a.sections.map((s, i) => (
            <motion.section key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5 }} data-testid={`insight-section-${i}`}>
              <h2 className="font-serif text-2xl sm:text-3xl font-extrabold uppercase tracking-tight mb-3">{s.heading}</h2>
              <p className="text-base text-ink/80 leading-relaxed">{s.body}</p>
            </motion.section>
          ))}
        </div>

        <div className="mt-14 pt-8 border-t border-ink/10">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slatem mb-3">sources</p>
          <ul className="space-y-1.5">
            {a.sources.map((s) => (
              <li key={s} className="text-sm text-slatem">{s}</li>
            ))}
          </ul>
        </div>

        <div className="mt-12 rounded-3xl border border-clay/30 bg-clay/5 p-6 sm:p-8 text-center">
          <h2 className="font-serif text-3xl font-extrabold uppercase tracking-tight mb-3">Are you Zinda Banda?</h2>
          <p className="text-sm text-slatem mb-6">Take the 2-minute test and find out — food first, safety gated.</p>
          <Link to="/check-in" data-testid="insight-cta"
            className="inline-flex items-center gap-2 rounded-full bg-clay text-bone px-7 py-3.5 text-sm font-semibold hover:bg-ink transition-colors min-h-[44px]">
            Test if you are Zinda Banda
          </Link>
        </div>
      </div>
    </main>
  );
}
